import DataTable from '@/components/features/data/DataTable'
import { grantColumns } from '@/components/features/data/DataTable/ColumnDefinitions/GrantColumns'
import {
  OrgAdminAccessSection,
  toOrgAdminRows,
} from '@/components/features/projects/OrgAdminAccessSection'
import Modal from '@/components/layout/Modal'
import ConfirmationDialog from '@/components/utils/ConfirmationDialog'
import { useClient } from '@/context/ClientContext'
import {
  PaginationAction,
  UseCursorPaginationQueryOptions,
  useCursorPaginationQuery,
} from '@/hooks/useCursorPaginationQuery'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { getTablePageSize } from '@/hooks/useUserPreferences'
import { fetchGrantsPaginated, fetchGroups } from '@/lib/clientHelpers'
import { PROJECT_ROLE_OPTIONS, isProjectAccessGrant } from '@/lib/projectRoles'
import { summaryKeys } from '@/lib/queryKeys'
import { getGroupName, getUserFullName } from '@/lib/utils'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { Select } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import type { Group } from '@droidmap/organisation-service-contract'
import {
  Grant,
  GrantPrincipalType,
  GrantResourceType,
  Role,
} from '@droidmap/permissions-service-contract'

const PREFERENCE_KEY = 'PROJECT_GRANTS'

/**
 * Generate a unique ID for a Grant for use in the DataTable.
 * Format matches DynamoDB pk#sk structure: resource:${resourceId}#principal:${principalId}:role:${role}
 */
const getGrantId = (
  grant: Pick<Grant, 'resourceId' | 'principalId' | 'role'>,
): string => {
  return `resource:${grant.resourceId}#principal:${grant.principalId}:role:${grant.role}`
}

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId/permissions',
)({
  beforeLoad: ({ context, params }) => {
    const queryOptions: UseCursorPaginationQueryOptions<
      Grant & { id: string }
    > = {
      preferenceKey: PREFERENCE_KEY,
      queryKey: ['projectGrants', params.projectId],
      queryFn: async (options) => {
        const result = await fetchGrantsPaginated(
          params.projectId,
          context.client.permissionsClient,
          { ...context.metadata, orgId: params.orgId },
          options,
        )
        // Org admin copies are shown read-only in their own section, built
        // from the org's grants (#3085). Filtering a server page here can
        // leave it short, but a project carries only a handful of copies.
        return {
          items: result.items
            .filter(isProjectAccessGrant)
            .map((g) => ({ ...g, id: getGrantId(g) })),
          next: result.next,
        }
      },
      initialPageSize: getTablePageSize(PREFERENCE_KEY),
      enabled: !!params.projectId,
    }

    return {
      grantsQueryOptions: queryOptions,
    }
  },
  component: ProjectPermissions,
  loader: async ({ context }) => {
    const { grantsQueryOptions } = context
    const pageSize: number = grantsQueryOptions.initialPageSize ?? 10

    // Prefetch first page - data will be cached and used by the hook
    await context.queryClient.fetchQuery({
      queryKey: [...grantsQueryOptions.queryKey, pageSize, 0],
      queryFn: () =>
        grantsQueryOptions.queryFn({
          limit: pageSize,
        }),
    })

    return { crumb: 'Permissions', metadata: context.metadata }
  },
})

function ProjectPermissions() {
  const { orgId, projectId } = Route.useParams()
  const { metadata, grantsQueryOptions } = Route.useRouteContext()
  const queryClient = useQueryClient()

  const { permissionsClient, organisationClient, userClient } = useClient()

  // Use org users for project-level permissions
  const { userMap } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  // Groups are still org-level - fetch all groups for the org
  const { data: groups = [] } = useQuery({
    queryKey: ['groups', orgId],
    queryFn: () => fetchGroups(organisationClient, orgId, metadata),
  })

  // Transform groups array to a groupId -> group map
  const groupMap = groups.reduce<Record<string, Group>>((acc, group) => {
    acc[group.id] = group
    return acc
  }, {})

  const orgAdmins = useQuery({
    queryKey: ['projectOrgAdmins', projectId],
    queryFn: () =>
      permissionsClient.listOrgAdminAccess({ resourceId: projectId }, metadata),
  })

  // Fetch project-level grants with pagination using query options from beforeLoad
  const {
    data: grants,
    isLoading: isLoadingGrants,
    isError,
    pagination,
    refresh,
  } = useCursorPaginationQuery(grantsQueryOptions)

  const [createGrantOpen, setCreateGrantOpen] = useState(false)
  const [selectedPrincipalType, setSelectedPrincipalType] =
    useState<GrantPrincipalType>(GrantPrincipalType.USER)
  const [selectedPrincipalId, setSelectedPrincipalId] = useState('')
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined)
  const [createGrantError, setCreateGrantError] = useState<string | null>(null)
  const [isCreatingGrant, setIsCreatingGrant] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [grantToDelete, setGrantToDelete] = useState<
    (Grant & { id: string }) | undefined
  >(undefined)

  const createGrantMutation = useMutation({
    mutationFn: async (event: React.FormEvent) => {
      event.preventDefault()
      setCreateGrantError(null)
      setIsCreatingGrant(true)
      if (!selectedPrincipalId || !selectedRole) {
        setCreateGrantError('Please select a principal and a role.')
        setIsCreatingGrant(false)
        return
      }
      try {
        const grant = await permissionsClient.createGrant(
          {
            orgId,
            principalId: selectedPrincipalId,
            principalType: selectedPrincipalType,
            resourceId: projectId,
            resourceType: GrantResourceType.PROJECT,
            role: selectedRole,
          },
          metadata,
        )
        return grant
      } catch (err) {
        setCreateGrantError(
          err instanceof Error ? err.message : 'Failed to create grant.',
        )
        setIsCreatingGrant(false)
        throw err
      }
    },
    onSuccess: (grant) => {
      setCreateGrantOpen(false)
      setSelectedPrincipalId('')
      setSelectedRole(undefined)
      setIsCreatingGrant(false)
      if (grant) {
        refresh({
          action: PaginationAction.CREATE,
          item: {
            ...grant,
            id: getGrantId(grant),
          },
        })
      }
      void queryClient.invalidateQueries({
        queryKey: summaryKeys.grants(projectId),
      })
    },
    onError: () => {
      setIsCreatingGrant(false)
    },
  })

  const deleteGrantMutation = useMutation({
    mutationFn: async () => {
      if (!grantToDelete) return
      await permissionsClient.deleteGrant({
        principalId: grantToDelete.principalId,
        resourceId: grantToDelete.resourceId,
        resourceType: grantToDelete.resourceType,
        role: grantToDelete.role,
      })
    },
    onSuccess: () => {
      if (grantToDelete) {
        refresh({ action: PaginationAction.DELETE, item: grantToDelete })
      }
      void queryClient.invalidateQueries({
        queryKey: summaryKeys.grants(projectId),
      })
      setDeleteDialogOpen(false)
      setGrantToDelete(undefined)
    },
  })

  return (
    <div className="space-y-8">
      <OrgAdminAccessSection
        orgId={orgId}
        rows={toOrgAdminRows(orgAdmins.data ?? [], userMap, groupMap)}
        isLoading={orgAdmins.isLoading}
        isError={orgAdmins.isError}
        onRetry={() => void orgAdmins.refetch()}
      />

      {/* Grants Table */}
      <DataTable
        data={grants}
        columns={grantColumns}
        title="Project access"
        description="Users and groups given a role on this project."
        isLoading={isLoadingGrants}
        onCreate={() => setCreateGrantOpen(true)}
        paginationConfig={pagination}
        tableOptions={{
          meta: {
            userMap,
            groupMap,
            hasError: isError,
            actions: {
              onDelete: (grant: Grant & { id: string }) => {
                setGrantToDelete(grant)
                setDeleteDialogOpen(true)
              },
            },
          },
        }}
      />
      {isError && (
        <div className="text-text mt-4">Failed to load project grants.</div>
      )}

      {/* Create Grant Modal */}
      <Modal
        title="Create Project Grant"
        description="Assign a role to an organisation member or group."
        icon={<PlusCircleIcon />}
        isOpen={createGrantOpen}
        onClose={() => {
          setCreateGrantOpen(false)
          setSelectedPrincipalId('')
          setSelectedRole(undefined)
          setCreateGrantError(null)
          setIsCreatingGrant(false)
        }}
      >
        <form onSubmit={createGrantMutation.mutate} className="space-y-6">
          <Select
            label="Principal Type"
            value={selectedPrincipalType}
            onChange={(e) => {
              setSelectedPrincipalType(e.target.value as GrantPrincipalType)
              setSelectedPrincipalId('')
            }}
          >
            <option value="user">Organisation Member</option>
            <option value="group">Organisation Group</option>
          </Select>
          <Select
            label={
              selectedPrincipalType === 'user'
                ? 'Select Member'
                : 'Select Group'
            }
            value={selectedPrincipalId}
            onChange={(e) => setSelectedPrincipalId(e.target.value)}
          >
            <option value="">-- Select --</option>
            {selectedPrincipalType === 'user'
              ? Object.entries(userMap).map(([userId]) => (
                  <option key={userId} value={userId}>
                    {getUserFullName(userMap, userId) || userId}
                  </option>
                ))
              : Object.entries(groupMap).map(([groupId]) => (
                  <option key={groupId} value={groupId}>
                    {getGroupName(groupMap, groupId) || groupId}
                  </option>
                ))}
          </Select>
          <Select
            label="Role"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as Role)}
          >
            <option value="">-- Select Role --</option>
            {PROJECT_ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
          {createGrantError && (
            <div className="text-text text-sm">{createGrantError}</div>
          )}
          <div className="mt-5 flex flex-row-reverse gap-2 sm:mt-4">
            <Button
              type="submit"
              variant="creative"
              disabled={
                !selectedPrincipalId || !selectedRole || isCreatingGrant
              }
              isLoading={isCreatingGrant}
            >
              Add
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateGrantOpen(false)
                setSelectedPrincipalId('')
                setSelectedRole(undefined)
                setCreateGrantError(null)
                setIsCreatingGrant(false)
              }}
              disabled={isCreatingGrant}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        title="Remove Project Grant"
        description="You're about to remove this permission grant. The grantee will lose the granted access immediately. Grants can be recreated if needed."
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setGrantToDelete(undefined)
        }}
        onConfirm={() => deleteGrantMutation.mutate()}
        confirmText="Remove"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteGrantMutation.isPending}
      />
    </div>
  )
}
